import React, { Component } from "react";
import { Dropdown, Form, OverlayTrigger, Popover } from "react-bootstrap";
import { LuImage, LuPaperclip, LuPlus, LuX } from "react-icons/lu";

import Tooltip from "blob_types/blobhub/compute/workflow/controls/common/tooltip";
import Utils from "context/utils";
import { autoGrow } from "../autoGrow";
import { ThreadImage, isImageBlock } from "../fileBlocks";
import {
  appendStaged, buildAlias, filesFromClipboard, filesFromDataTransfer, insertToken, interleaveContent,
  stripToken, tokenSpans
} from "./attachments";
import { contentToDraft, normalizeContent, opaqueBlocks, sameContent } from "./content";
import "./index.css";

/** Rows the field grows to before it scrolls. */
const MAX_ROWS = 6;

// The `+`. A plain button rather than Dropdown's own toggle, so the circular styling stays while
// react-bootstrap keeps the click and keyboard handling.
const AttachToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="playground-thread-composer-attach"
    onClick={(e) => { e.preventDefault(); onClick(e); }}
    aria-label="Attachment menu"
  >
    <LuPlus />
  </button>
));

/** A chip's contribution to the content, once it carries an alias. */
function fileBlockFor(chip) {
  return {
    type: "file",
    file: { alias: chip.alias, name: chip.name, content_type: chip.contentType, size: chip.size }
  };
}

// FileReader, not File.prototype.arrayBuffer(): the latter is absent from some runtimes this bundle
// ships to, and from jsdom.
function readFileBytes(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Composer — the one control a person types prose an agent will read into.
 *
 * **Its value is content**: the ordered `text`/`file` block array a thread item and a description are
 * both made of. It edits that as one string with a `[name]` token where each file sits, and the token
 * never leaves this component. See `docs/specs/2026-10-13-mission-control-attachments.md` §2.
 *
 * **The commit control is a prop** (`commit`, a render prop) and may be absent — which is the whole of
 * what distinguishes the thread's send icon, a cell's verb-and-caret, and a brief that commits on blur.
 * Enter commits only where there is one; without, it is a newline, because a brief is prose.
 *
 * Props: see the spec's §2.4. In short — `value`/`onChange` carry content, `fileBase`/`upload` say where
 * bytes go, `uploadWhen` says when, `commit`/`onCommit` are the commit control, `owner` names the record
 * the content belongs to (opaque here, echoed back with every report), `maxRows`/`maxHeight` cap the field,
 * and the rest is chrome.
 */
export class Composer extends Component {
  constructor(props) {
    super(props);
    const draft = contentToDraft(props.value);
    // `opaque`: blocks the field cannot show. They ride through every report and commit untouched, after the
    // edited content, rather than being dropped on save.
    this.state = { text: draft.text, chips: draft.chips, opaque: opaqueBlocks(props.value), committing: false };
    this.textareaRef = React.createRef();
    this.fileInputRef = React.createRef();
    // Object URLs for the staged-image hover previews, one per chip id. Created lazily from the local
    // File (no upload) and revoked when the chip leaves `chips`.
    this.previewUrls = new Map();
    // What this composer last handed to onChange. `props.value` coming back as anything else is the
    // caller re-seeding it; coming back as this is the echo of its own report.
    this.reported = normalizeContent(props.value);
    // Bumped by every re-seed. An async pass that began under an earlier value must not deliver its result.
    this.generation = 0;
    // Resolvers of the mark() calls React has not flushed yet — see componentWillUnmount.
    this.marks = new Set();
  }

  componentDidMount() {
    this.grow();
  }

  componentDidUpdate(prev) {
    if ( prev.value !== this.props.value && !sameContent(this.props.value, this.reported) ) this.reseed();
    this.cleanupPreviewUrls();
    this.grow();
  }

  componentWillUnmount() {
    this.unmounted = true;
    // Release every pending mark(): React will not run a setState callback on a dead instance, and an
    // upload in flight is awaiting one. The bytes are already on the server — the send has to finish.
    this.marks.forEach((resolve) => resolve());
    this.marks.clear();
    this.previewUrls.forEach((url) => { try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ } });
    this.previewUrls.clear();
  }

  /**
   * Value
   */

  // The value is the truth. A caller handing this composer content it did not report — a switched
  // selection, a cleared draft — re-seeds it, and staged files that never uploaded go with the old one.
  reseed() {
    const next = normalizeContent(this.props.value);
    const draft = contentToDraft(next);
    this.reported = next;
    this.generation += 1;
    this.setState({ text: draft.text, chips: draft.chips, opaque: opaqueBlocks(next), committing: false });
  }

  // What onChange carries: the text with each ALIASED chip's block spliced in at its token. A chip
  // still waiting to upload has no block to report, so it is absent until it has one. `owner` is the
  // caller's own opaque token for the record this draft belongs to, echoed back so a report can be
  // routed rather than assumed to be about whatever is on screen when it arrives.
  report(reason) {
    const content = this.withOpaque(this.contentFor(this.state.text, this.state.chips));
    this.reported = content;
    if ( this.props.onChange ) this.props.onChange(content, { reason, owner: this.props.owner });
  }

  // A report for a record this composer has since been re-seeded away from — see uploadNow. It is
  // built from a snapshot of that record's draft, and it does NOT become `this.reported`: what the
  // caller holds for the record now on screen has not changed.
  reportFor(draft, owner) {
    if ( !this.props.onChange ) return;
    const content = this.contentFor(draft.text, draft.chips).concat(draft.opaque);
    this.props.onChange(content, { reason: "attach", owner });
  }

  // The `[name]` token is an editor affordance and must never be emitted. A chip without an alias has no
  // block to stand for it, so its token is removed from the text first; interleaveContent would keep it as prose.
  contentFor(text, chips) {
    let clean = text;
    chips
      .map((chip, i) => ({ chip, occurrence: chips.slice(0, i).filter((c) => c.name === chip.name).length }))
      .filter(({ chip }) => !chip.alias)
      .reverse()
      .forEach(({ chip, occurrence }) => { clean = stripToken(clean, chip.name, occurrence); });
    return interleaveContent(clean, chips.filter((chip) => chip.alias).map(fileBlockFor));
  }

  withOpaque(content) {
    return content.concat(this.state.opaque);
  }

  grow() {
    autoGrow(this.textareaRef.current, this.props.maxRows || MAX_ROWS, this.props.maxHeight);
  }

  /**
   * State the placement reads through `commit`
   */

  canAttach() {
    return !!(this.props.upload && this.props.fileBase && !this.props.disabled);
  }

  // WHETHER THE ATTACH IS THE WRITE, and it is not only a prop. A commit-less placement has no second
  // moment: `commit()` is the only other upload trigger, it needs `onCommit` to do anything and `commit`
  // to be reachable at all, so without both the chips would keep `alias: null` and content.js would drop
  // every file part — prose saved, attachment evaporated, no error. So the absence of a commit control
  // IMPLIES stage mode rather than merely requiring the prop, and a placement cannot get it wrong by
  // omission. Passing "stage" with a commit control is still meaningful: upload on attach, commit later.
  uploadsOnStage() {
    return "stage" === this.props.uploadWhen || !(this.props.commit && this.props.onCommit);
  }

  // This composer's half of "can commit"; the placement ANDs its own in. `allowEmpty` and FIELDLESS both
  // ask for no content here: the placement decides what is too little (its prose comes from answers this
  // control never saw).
  ready() {
    const chips = this.state.chips;
    const sendable = chips.some((chip) => "rejected" !== chip.status);
    const rejected = chips.some((chip) => "rejected" === chip.status);
    // Stage mode has no upload pass in commit(): a chip still uploading, or failed, has no block to send.
    const unsent = this.uploadsOnStage() && chips.some((c) => "rejected" !== c.status && !c.alias);
    const placed = this.placed();
    const content = this.props.fieldless || this.props.allowEmpty || this.state.text.trim() || sendable
      || this.state.opaque.length > 0;
    return !!(content && placed && !rejected && !unsent && !this.state.committing && !this.props.disabled);
  }

  // The placement's own half, here rather than in its render prop so the button, commit() and Enter share it.
  // Pure and cheap: ready() runs on every render. commit() asks again after the upload, because the placement
  // can change under an upload in flight (a verb switched while the bytes travel).
  placed() {
    return !this.props.gate || !!this.props.gate({ hasFiles: this.hasFiles() });
  }

  /** Whether a file is part of this draft — a placement cannot see the chips to ask. */
  hasFiles() {
    return this.state.chips.some((chip) => "rejected" !== chip.status);
  }

  /**
   * Committing
   */

  // Upload whatever still needs it, interleave, hand the content over. On an upload failure nothing is
  // committed and the failing chip stays, retriable; on a refusal the draft stays exactly as it was.
  commit = () => {
    if ( !this.ready() || !this.props.onCommit ) return Promise.resolve(false);
    this.setState({ committing: true });
    const generation = this.generation;
    // Stage mode has already uploaded: its chips carry their aliases and ready() refuses while one does not.
    const pass = this.uploadsOnStage()
      ? Promise.resolve({ ok: true, chips: this.state.chips })
      : this.uploadStaged(generation);
    return pass.then(({ ok, chips }) => {
      // A re-seed during the upload pass means the draft is another record's now; deliver nothing.
      if ( generation !== this.generation ) return false;
      // Refused now, the draft and uploaded files stay; a retry reuses them only within the same `fileBase`,
      // and whether a session switch re-seeds the composer is UNVERIFIED. `ok &&`: a failed upload skips this
      // re-check and `!ok` below refuses.
      if ( ok && !this.placed() ) {
        this.setState({ committing: false });
        return false;
      }
      // Fieldless, `[]` is a legitimate value to hand over: the placement assembles the body, and its prose
      // came from the answers rather than from here. With a field, nothing to send is nothing to send.
      // The pass's chips, not the state's: see uploadStaged. `text` and `opaque` are read from state, which
      // an unmount does not change — only the aliases were in flight.
      const content = ok ? this.withOpaque(this.contentFor(this.state.text, chips)) : [];
      if ( !ok || (0 === content.length && !this.props.fieldless && !this.props.allowEmpty) ) {
        this.setState({ committing: false });
        return false;
      }
      // new Promise, so a synchronous throw from onCommit lands in the rejection handler below.
      return new Promise((resolve) => resolve(this.props.onCommit(content))).then((taken) => {
        if ( generation !== this.generation ) return false;
        if ( false === taken ) {
          this.setState({ committing: false });
          return false;
        }
        this.clear();
        return true;
      }, (error) => {
        if ( generation !== this.generation ) return false;
        this.setState({ committing: false });
        this.fail((error && error.message) || "Failed to send.");
        return false;
      });
    });
  };

  // Sequential, in staging order, skipping a chip that already carries an alias (a prior attempt) and
  // one removed since this snapshot. The first failure marks that chip and stops: nothing already
  // uploaded is undone, so the next commit re-uploads only what is left.
  //
  // IT RETURNS THE CHIPS, aliases filled in LOCALLY, and commit() builds the content from those rather than
  // from React state. A send can outlive this instance — the widget removed, its session or layout switched,
  // while the bytes travel — and a dead instance receives no setState, so content rebuilt from state would
  // be missing exactly the aliases this pass just minted: the token stripped, the file part dropped, the
  // message posted without the attachment it was sent with. Main accumulated the same blocks in a local
  // variable for the same reason.
  async uploadStaged(generation) {
    let chips = this.state.chips;
    for ( const chip of chips.slice() ) {
      if ( generation !== this.generation ) return { ok: false, chips: [] };
      if ( "rejected" === chip.status ) continue;
      // Removed since this pass began: the person asked for it not to be sent, so it neither uploads nor
      // contributes a block. Its token left with it, in removeStagedFile.
      if ( !this.state.chips.some((c) => c.id === chip.id) ) {
        chips = chips.filter((c) => c.id !== chip.id);
        continue;
      }
      if ( chip.alias ) continue;
      const alias = await this.store(chip);
      if ( !alias ) return { ok: false, chips: [] };
      chips = chips.map((c) => (c.id === chip.id ? { ...c, status: "uploaded", alias } : c));
    }
    return { ok: true, chips };
  }

  // One chip's bytes, at the alias this composer mints. Minting itself can throw — generateUniqueId
  // needs crypto.randomUUID, absent in an insecure context — which is as much a failed upload as a
  // refused POST and must not escape as a rejection nobody handles.
  async store(chip) {
    try {
      const alias = buildAlias(this.props.fileBase, chip.name);
      const bytes = await readFileBytes(chip.file);
      await this.props.upload({ alias, name: chip.name, contentType: chip.contentType, bytes,
                                file: chip.file });
      await this.mark(chip.id, { status: "uploaded", error: null, alias });
      return alias;
    } catch (error) {
      await this.mark(chip.id, { status: "failed", error: (error && error.message) || "Upload failed." });
      return null;
    }
  }

  // Resolves once React has the change, so a caller reading this.state after it reads the new value — and
  // resolves ANYWAY once this instance is gone, where React runs no callback at all. `store()` awaits this,
  // so a mark that never resolves is an upload that never finishes and a send that never posts. The rule is
  // the one main carried in the code this component was lifted from: skip the setState, not the chip's
  // outcome — the upload already happened, mounted or not.
  mark(id, changed) {
    if ( this.unmounted ) return Promise.resolve();
    return new Promise((resolve) => {
      this.marks.add(resolve);
      this.setState((prev) => ({
        chips: prev.chips.map((chip) => (chip.id === id ? { ...chip, ...changed } : chip))
      }), () => { this.marks.delete(resolve); resolve(); });
    });
  }

  fail(message) {
    if ( this.props.onError ) this.props.onError(message);
    else console.error(`Composer: ${message}`);
  }

  clear() {
    this.reported = [];
    this.setState({ text: "", chips: [], opaque: [], committing: false }, () => {
      const el = this.textareaRef.current;
      if ( el ) el.focus();
      this.grow();
      if ( this.props.onChange ) this.props.onChange([], { reason: "edit" });
    });
  }

  /**
   * Staging
   */

  // Wraps the pure appendStaged() and inserts a `[name]` token per new chip at the caret. The caret is read
  // before setState (the textarea's value has not moved yet) and threaded through each insertion.
  stageFiles = (fileList) => {
    const ta = this.textareaRef.current;
    const caretAtCall = (ta && "number" === typeof ta.selectionStart) ? ta.selectionStart : null;
    let staged = [];
    this.setState((prev) => {
      const chips = appendStaged(prev.chips, fileList);
      staged = chips.slice(prev.chips.length);
      let text = prev.text;
      let caret = null === caretAtCall ? text.length : caretAtCall;
      // Fieldless: there is no text to place a file in, and a token left in state would outlive the chip.
      if ( !this.props.fieldless ) {
        staged.forEach((chip) => {
          const inserted = insertToken(text, caret, chip.name);
          text = inserted.text;
          caret = inserted.caret;
        });
      }
      this._caret = caret;
      return { chips, text };
    }, () => {
      this.restoreCaret();
      this.report("attach");
      if ( this.uploadsOnStage() ) this.uploadNow(staged);
    });
  };

  // Stage mode — see uploadsOnStage(). A brief has no commit control, so the write that would carry the
  // bytes never comes. The attach IS the write, and the reference it reports has to be real.
  //
  // A re-seed during the upload does NOT cancel the report: the bytes landed under an alias belonging to
  // the record this draft came from, so the reference is delivered against the owner captured here, built
  // from a snapshot of that draft. Dropping the report would strand the bytes — stored, paid for, and
  // named by nothing. What a re-seed does cancel is the RENDER: the composer goes on showing the record
  // that replaced it, which is why the snapshot exists instead of reading state back.
  uploadNow(chips) {
    const generation = this.generation;
    const owner = this.props.owner;
    let draft = this.draftSnapshot();
    return chips.reduce((chain, chip) => chain.then(() => {
      if ( chip.alias ) return null;
      return this.storeAndReport(chip, generation, owner, draft).then((next) => { draft = next; });
    }), Promise.resolve());
  }

  // The draft as it stands. Taken before an upload, so a pass that outlives a re-seed can still report
  // the content of the record it began under instead of reading another record's state back.
  draftSnapshot() {
    return { text: this.state.text, chips: this.state.chips, opaque: this.state.opaque };
  }

  // One chip's bytes, then the reference, to whichever record it belongs to. Returns the draft to carry
  // into the next chip of the same pass — live state while the record is still on screen, the snapshot
  // plus this chip's alias once it is not.
  //
  // `!this.unmounted` is the second half of the same rule commit() follows: report() reads React state, and
  // a dead instance never received this chip's alias, so reporting from state there would hand the panel the
  // prose WITHOUT the file — which, when the prose has not changed, is skipped as a no-op write and the
  // bytes are stranded. A brief has no commit, so this is the only path its reference can take.
  storeAndReport(chip, generation, owner, draft) {
    return this.store(chip).then((alias) => {
      if ( !this.unmounted && generation === this.generation ) {
        this.report("attach");
        return this.draftSnapshot();
      }
      if ( !alias ) return draft;
      const next = { ...draft,
                     chips: draft.chips.map((c) => (
                       c.id === chip.id ? { ...c, status: "uploaded", alias } : c)) };
      this.reportFor(next, owner);
      return next;
    });
  }

  /** In stage mode a failed chip has no Send to retry with, so the chip itself is the retry. */
  retryChip = (id) => {
    const chip = this.state.chips.find((c) => c.id === id);
    if ( !chip || "failed" !== chip.status || !chip.file ) return Promise.resolve();
    return this.storeAndReport(chip, this.generation, this.props.owner, this.draftSnapshot());
  };

  // Strips the removed chip's token — its occurrence is its index among the staged chips sharing that
  // name, so removing the second same-named chip strips the second token.
  removeStagedFile = (id) => {
    this.setState((prev) => {
      const chips = prev.chips;
      const target = chips.find((c) => c.id === id);
      let text = prev.text;
      if ( target && target.name ) {
        const sameName = chips.filter((c) => c.name === target.name);
        const occurrence = sameName.findIndex((c) => c.id === id);
        text = stripToken(prev.text, target.name, occurrence < 0 ? 0 : occurrence);
      }
      return { chips: chips.filter((c) => c.id !== id), text };
    }, () => this.report("remove"));
  };

  // The hidden input's template: read the files off the event, THEN reset the value so choosing the same
  // file again still fires. `event.target.files` is a LIVE FileList and clearing the value empties that
  // same list, so the snapshot has to come first.
  handleFileInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = null;
    if ( 0 === files.length ) return;
    this.stageFiles(files);
  };

  // filesFromDataTransfer() answers null when the drag carries no "Files" type — a widget-move drag, with
  // a text/plain payload. Only a real file drag is claimed; anything else passes through untouched.
  handleDragOver = (e) => {
    if ( !this.canAttach() || !filesFromDataTransfer(e.dataTransfer) ) return;
    e.preventDefault();
    e.stopPropagation();
  };

  handleDrop = (e) => {
    if ( !this.canAttach() ) return;
    const files = filesFromDataTransfer(e.dataTransfer);
    if ( !files ) return;
    e.preventDefault();
    e.stopPropagation();
    this.stageFiles(files);
  };

  // A plain-text paste is left native, so the textarea still receives it.
  handlePaste = (e) => {
    if ( !this.canAttach() ) return;
    const files = filesFromClipboard(e.clipboardData);
    if ( 0 === files.length ) return;
    e.preventDefault();
    this.stageFiles(files);
  };

  /**
   * Editing
   */

  // Enter commits where there is a commit control. Backspace/Delete on an attached file's token removes the
  // whole token (and one adjacent space); a selection touching tokens expands to clear them. Typing inside
  // a token needs no handler: it breaks the bracket match.
  handleKeyDown = (e) => {
    if ( "Enter" === e.key && !e.shiftKey && this.props.commit ) {
      e.preventDefault();
      this.commit();
      return;
    }
    if ( "Backspace" !== e.key && "Delete" !== e.key ) return;
    const ta = e.target;
    if ( !ta || "number" !== typeof ta.selectionStart ) return;
    const text = this.state.text;
    const names = this.state.chips.map((chip) => chip.name);
    const spans = tokenSpans(text, names);
    if ( 0 === spans.length ) return;
    const selStart = ta.selectionStart;
    const selEnd = ta.selectionEnd;

    if ( selStart === selEnd ) {
      const span = "Backspace" === e.key
        ? spans.find((s) => selStart > s.start && selStart <= s.end)
        : spans.find((s) => selStart >= s.start && selStart < s.end);
      if ( !span ) return;
      e.preventDefault();
      let from = span.start;
      let to = span.end;
      if ( "Backspace" === e.key && from > 0 && " " === text[from - 1] ) from -= 1;
      else if ( "Delete" === e.key && to < text.length && " " === text[to] ) to += 1;
      this.setTextAndCaret(text.slice(0, from) + text.slice(to), from);
      return;
    }

    const clipped = spans.filter((s) => s.start < selEnd && s.end > selStart);
    if ( 0 === clipped.length ) return;
    e.preventDefault();
    const from = Math.min(selStart, ...clipped.map((s) => s.start));
    const to = Math.max(selEnd, ...clipped.map((s) => s.end));
    this.setTextAndCaret(text.slice(0, from) + text.slice(to), from);
  };

  // The text is authoritative: a chip whose `[name]` token has gone is dropped. Returns the original array
  // when nothing changed. FIELDLESS has no text, so a pass would drop every chip; `×` is its only removal.
  chipsForText(text, chips) {
    if ( this.props.fieldless ) return chips;
    if ( !chips || 0 === chips.length ) return chips;
    const present = new Set(tokenSpans(text, chips.map((chip) => chip.name)).map((span) => span.name));
    const kept = chips.filter((chip) => present.has(chip.name));
    return kept.length === chips.length ? chips : kept;
  }

  setTextAndCaret(text, caret) {
    this._caret = caret;
    this.setState((prev) => ({ text, chips: this.chipsForText(text, prev.chips) }), () => {
      this.restoreCaret();
      this.report("edit");
    });
  }

  restoreCaret() {
    const el = this.textareaRef.current;
    if ( el && "number" === typeof this._caret ) {
      el.focus();
      if ( el.setSelectionRange ) el.setSelectionRange(this._caret, this._caret);
    }
    this._caret = null;
  }

  handleInput = (e) => {
    const value = e.target.value;
    // Reconcile on every edit: a native delete, cut or type-over that removes a token drops its file too.
    this.setState((prev) => ({ text: value, chips: this.chipsForText(value, prev.chips) }),
      () => this.report("edit"));
    // Size on the way through — waiting for the re-render leaves the field a keystroke behind its text.
    autoGrow(e.target, this.props.maxRows || MAX_ROWS, this.props.maxHeight);
  };

  /**
   * Rendering
   */

  // The staged image's own File is previewable locally — no upload, no fetch, and no new security
  // surface, since it is the user's own file in their own tab. Cached per chip, and guarded so a runtime
  // without createObjectURL simply gets no thumbnail.
  previewUrlFor(chip) {
    if ( !chip.file || "undefined" === typeof URL || "function" !== typeof URL.createObjectURL ) return null;
    if ( !this.previewUrls.has(chip.id) ) {
      try { this.previewUrls.set(chip.id, URL.createObjectURL(chip.file)); }
      catch (e) { return null; }
    }
    return this.previewUrls.get(chip.id);
  }

  // Revoke the URL of any chip no longer staged — removal and clear-after-commit both land here, so no
  // path leaks. Called after every update.
  cleanupPreviewUrls() {
    if ( 0 === this.previewUrls.size ) return;
    const live = new Set(this.state.chips.map((chip) => chip.id));
    this.previewUrls.forEach((url, id) => {
      if ( !live.has(id) ) {
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
        this.previewUrls.delete(id);
      }
    });
  }

  // What a chip is, before it is sent: a thumbnail for an image, a type icon otherwise, and the reason a
  // rejected or failed one cannot go. Bytes already on the server render through fileBlocks, which is
  // also where the only decision about what may be shown as an image lives.
  renderPreview(chip, isImage) {
    const local = isImage ? this.previewUrlFor(chip) : null;
    const remote = (isImage && !chip.file && chip.alias && this.props.fileContext)
      ? fileBlockFor(chip) : null;
    return (
      <Popover id={"attach-preview-" + chip.id} className="playground-thread-attach-popover">
        <Popover.Content>
          {local && <img src={local} alt={chip.name} className="playground-thread-attach-preview-img" />}
          {!local && remote && isImageBlock(remote) &&
            <ThreadImage fileContext={this.props.fileContext} block={remote} />
          }
          {!local && !remote &&
            <div className="playground-thread-attach-preview-icon">
              {isImage ? <LuImage /> : <LuPaperclip />}
            </div>
          }
          <div className="playground-thread-attach-preview-name">{chip.name}</div>
          <div className="playground-thread-attach-preview-sub">
            {chip.contentType || "unknown type"}{chip.size ? " · " + Utils.formatBytes(chip.size, 1) : ""}
          </div>
          {chip.error && <div className="playground-thread-attach-preview-err">{chip.error}</div>}
        </Popover.Content>
      </Popover>
    );
  }

  renderChip(chip) {
    const isImage = !!(chip.contentType && chip.contentType.startsWith("image/"));
    const ChipIcon = isImage ? LuImage : LuPaperclip;
    // "rejected" (client-side validation) and "failed" (an upload failure) both read as failed; the
    // chip's title and its preview carry the specific reason.
    const isFailed = "rejected" === chip.status || "failed" === chip.status;
    const retriable = "failed" === chip.status && this.uploadsOnStage();
    return (
      <OverlayTrigger key={chip.id} placement="top" trigger={["hover", "focus"]}
        overlay={this.renderPreview(chip, isImage)}>
        <button
          type="button"
          className={"playground-thread-composer-attached" + (isFailed ? " is-failed" : "")}
          title={chip.error || undefined}
          onClick={retriable ? () => this.retryChip(chip.id) : undefined}
        >
          <ChipIcon className="playground-thread-composer-attached-icon" />
          <span className="playground-thread-composer-attached-label">{chip.name}</span>
          <Tooltip text="Remove">
            <span
              role="button"
              tabIndex={0}
              className="playground-thread-composer-attached-remove"
              onClick={(e) => { e.stopPropagation(); this.removeStagedFile(chip.id); }}
              onKeyDown={(e) => {
                if ( "Enter" === e.key || " " === e.key ) {
                  e.preventDefault();
                  e.stopPropagation();
                  this.removeStagedFile(chip.id);
                }
              }}
            >
              <LuX />
            </span>
          </Tooltip>
        </button>
      </OverlayTrigger>
    );
  }

  render() {
    const { commit, chipsExtra, menuExtra, placeholder, disabled, textareaClassName, onBlur,
            autoFocus, fieldless } = this.props;
    const attach = this.canAttach();
    return (
      <>
        {attach &&
          <input type="file" multiple ref={this.fileInputRef} style={{ display: "none" }}
            onChange={this.handleFileInputChange} />
        }
        {this.state.chips.map((chip) => this.renderChip(chip))}
        {chipsExtra}
        <div className="bh-composer-shell" onDragOver={this.handleDragOver} onDrop={this.handleDrop}>
          {(attach || menuExtra) &&
            <Dropdown drop="up">
              <Dropdown.Toggle as={AttachToggle} />
              <Dropdown.Menu>
                {attach &&
                  <Dropdown.Item onClick={() => this.fileInputRef.current.click()}>
                    <LuPaperclip />&nbsp;&nbsp;Attach Files
                  </Dropdown.Item>
                }
                {menuExtra}
              </Dropdown.Menu>
            </Dropdown>
          }
          {/* Fieldless: the shell holds the `+` and the commit and nothing between them — the prose this
              shape sends is derived from its answers, so there is nothing to type. See the spec's §2.7. */}
          {!fieldless &&
            <Form.Control
              as="textarea"
              rows={1}
              ref={this.textareaRef}
              className={`bh-composer-textarea${textareaClassName ? ` ${textareaClassName}` : ""}`}
              placeholder={placeholder}
              value={this.state.text}
              disabled={disabled}
              autoFocus={autoFocus}
              style={{ resize: "none", overflow: "hidden" }}
              onChange={this.handleInput}
              onKeyDown={this.handleKeyDown}
              onPaste={this.handlePaste}
              onBlur={onBlur}
            />
          }
          {commit && commit({ ready: this.ready(), committing: this.state.committing,
                              hasFiles: this.hasFiles(), commit: this.commit })}
        </div>
      </>
    );
  }
}

export default Composer;
